/**
 * How ⌘K finds a row: by its name, by the words people actually type for it, and
 * in an order where the row that answers a word outranks one that merely contains it.
 *
 * A usability session typed settings, api, help, fork, history and known into the
 * palette and got nothing back for any of them — every one named something the app
 * has, under a title that used different words — and "key" ranked Keyboard shortcuts
 * first, because the search could only see that "key" is a prefix of "keyboard". The
 * words below are what those rows are called by the people looking for them. They
 * are never printed: a row's title is its name, and a second visible vocabulary is
 * how one thing comes to have two names (the Library had exactly that).
 *
 * Pure and outside the component, so the ranking is pinned by `paletteSearch.test.ts`
 * against the real keyword table rather than against a copy of it.
 */

import { searchRecent } from '../core/persist'

/**
 * The hidden words, per palette row id. Space-separated; matched as whole words.
 *
 * Each word belongs to the row that ANSWERS it, and a word two rows could answer is
 * given to both only when both are true answers — "help" opens the first-run sheet and
 * the shortcut sheet, and either is what somebody asking for help might want.
 */
export const PALETTE_KEYS: Readonly<Record<string, string>> = {
  // The keys sheet is the settings: the model, the provider keys, the routes.
  'cmd-model': 'settings setting preferences api key keys connect provider account model',
  // The first-run sheet, under the words of somebody who dismissed it by accident
  // and wants back to the start (D335). "method" stays, for the row's old title.
  'cmd-method':
    'help about how guide intro method start started setup begin onboarding first welcome',
  'cmd-shortcuts': 'help hotkeys bindings',
  'cmd-replay': 'history rewind replay playback timeline',
  'cmd-undo': 'history rewind back',
  'cmd-redo': 'history forward',
  'cmd-branch': 'fork branch history rewind step',
  'cmd-fork': 'fork copy duplicate clone',
  'cmd-rename': 'rename name title label call retitle',
  // The lab holds the versions of the argument, dated — "as known on this date".
  'cmd-research-lab': 'history known versions knowledge audit protocol evidence lab',
  'cmd-survey': 'known literature field prior',
  'cmd-export-md': 'report download pdf markdown json share link print export',
  'cmd-library': 'explorations saved open files storage stored',
  // The search sheet's door. "history" and "chats" are what other apps' ⌘K
  // calls the list of past work (D335).
  'cmd-search-explorations': 'find search explorations chats history switch',
  't-all': 'filters switches reset unhide colors colours everything',
  'cmd-tune': 'budget cap ceiling spend moves',
  // The door's old name, "Your own sources", for a reader who still types it (D390).
  'cmd-sources': 'documents corpus files pdf upload own mine my',
  // The scan's rows under the tab's old name, which a returning reader still types.
  'cmd-bridge': 'breakthroughs breakthrough synthesis bridge bridges scan pairs',
  'cmd-bridge-across': 'breakthroughs breakthrough synthesis bridge bridges scan library',
  // The conjecture line's three, under the words a reader brings to them. None
  // says "new": the pass may not claim it, and neither may a word that finds it.
  // "insight" is the press's own word (D333) and is listed so the row outranks the
  // Recent rows whose sub counts the insight channel; "conjecture" stays, the
  // way cmd-bridge keeps the tab's old name, for a reader who still types it.
  'op-conjecture':
    'insight insights conjecture conjectures hypothesis hypotheses speculate speculation guess idea ideas unstated',
  'op-prior-art': 'precedent published already said literature papers existing citation',
  'op-sweep':
    'questions anomaly anomalies dispute disputes controversy debate unresolved replication',
}

export interface Searchable {
  title: string
  sub?: string
  /** The row's full text where `sub` is an abbreviation of it. */
  hay?: string
  group: string
  keys?: string
}

/** A named keyword outranks everything; a title outranks a subtitle. */
const KEYWORD = -100
const KEYWORD_PREFIX = 30
const SUBTITLE = 40

/**
 * Where a row ranks for a query, lower first, or null when it does not match.
 *
 * Four tiers. A row whose hidden words include the query IS the answer to it, so
 * it leads — that is what puts the keys sheet above Keyboard shortcuts for "key".
 * Then a title that contains the query, earlier first, which was the whole ranking
 * before. Then a hidden word the query begins ("sett"), at three letters or more so
 * two keystrokes do not light up half the list. Then anything in the subtitle.
 */
export function paletteScore(it: Searchable, needle: string): number | null {
  const words = (it.keys ?? '').toLowerCase().split(/\s+/).filter(Boolean)
  if (words.includes(needle)) return KEYWORD
  const title = it.title.toLowerCase()
  const inTitle = title.indexOf(needle)
  if (inTitle >= 0) return inTitle
  if (needle.length >= 3 && words.some((w) => w.startsWith(needle))) return KEYWORD_PREFIX
  const idx = `${title} ${it.hay ?? it.sub ?? ''} ${it.group}`.toLowerCase().indexOf(needle)
  return idx < 0 ? null : idx + SUBTITLE
}

/** The rows that match, best first, stable among equals — the list keeps its own
 *  order where nothing separates two rows. */
export function rankPalette<T extends Searchable>(items: readonly T[], query: string): T[] {
  const needle = query.trim().toLowerCase()
  return items
    .map((it, i) => ({ it, i, score: paletteScore(it, needle) }))
    .filter((x): x is { it: T; i: number; score: number } => x.score !== null)
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .map((x) => x.it)
}

/**
 * The stored explorations a palette row is offered for, other than the open one.
 *
 * Four newest with nothing typed, as the palette always listed them. With a query,
 * every stored exploration the search sheet would find, ranked its way
 * (`searchRecent`: name and seed before project) and capped at six (D335). The
 * palette used to offer only the four newest whatever was typed, so the name of
 * last week's exploration found nothing, and the empty state offered to state it
 * as a new claim: a duplicate of work that was already there. ⌘K is the box that
 * reaches everything in every app a reader brings habits from.
 */
export function paletteExplorations<
  T extends { id: string; seed: string; project?: string; name?: string },
>(stored: readonly T[], openId: string, q: string): T[] {
  const others = stored.filter((r) => r.id !== openId)
  return q.trim() ? searchRecent(others, q).slice(0, 6) : others.slice(0, 4)
}
